<script setup>
import {ref, computed, onMounted} from 'vue'

import SiteTreeNavigationMenu from '@/common/site-tree/SiteTreeNavigationMenu.vue'
import SiteTree from '@/common/site-tree/SiteTree.vue'
import Button from "@/common/Button.vue"
import {usePageTemplatesContentAreasStore} from '@/stores/pageTemplatesContentAreas'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useSiteTreeStore} from '@/stores/siteTree'

const props = defineProps({
    options: {
        type: Object,
        required: true
    }
})

const emit = defineEmits(['hide-modal'])

const pageTemplatesContentAreasStore = usePageTemplatesContentAreasStore()
const pageContentDataStore = usePageContentDataStore()

// --- State ---

const siteTreeId = 'siteTreeModal'
const siteTreeStore = useSiteTreeStore(siteTreeId)
const originalPropagatedPageIds = ref([])

// Unpack options for readability
const {
    showContent = (props.options.selectContentMode || false),
    pageContentLinkId,
    pageId,
    onPageSelect,
    onContentSelect,
    onSave,
    selectContentMode,
    propagatedContentId
} = props.options

// --- Computed ---

/**
 * Checks if propagation selections have changed by comparing
 * sorted JSON strings of page IDs.
 */
const isSaveBtnDisabled = computed(() => {
    const pageIds = siteTreeStore.propagationData
        .map(o => o.pageId)
        .sort()

    return JSON.stringify(originalPropagatedPageIds.value) === JSON.stringify(pageIds)
})

const pageContentLinkItem = computed(() => {
    if (pageContentLinkId) {
        return pageContentDataStore.items.find(o => o.id === pageContentLinkId)
    }
    return null
})

// --- Methods ---

const setSelectedVars = () => {
    if (pageId) {
        siteTreeStore.setPageId(pageId)
        siteTreeStore.setExpandedPageIds(pageId)
    } else if (pageContentLinkId && pageContentLinkItem.value) {
        siteTreeStore.setContentId(pageContentLinkId)
        const pid = pageContentLinkItem.value.pageId
        siteTreeStore.addExpandedPageId(pid)
        siteTreeStore.setExpandedPageIds(pid)
    }
}

const saveSelectedPageIds = () => {
    const propagationData = siteTreeStore.propagationData
    if (onSave) {
        onSave({propagationData})
    }
    emit('hide-modal')
}

// --- Lifecycle ---

onMounted(async () => {
    // 1. Initialize the store logic
    await siteTreeStore.init({
        selectContentMode,
        showContent,
        propagatedContentId,
        onPageSelect: (pid) => {
            if (onPageSelect) {
                const bool = onPageSelect(pid)
                if (bool || bool === undefined) emit('hide-modal')
            }
        },
        onContentSelect: (pcid) => {
            if (onContentSelect) {
                const bool = onContentSelect(pcid)
                if (bool || bool === undefined) emit('hide-modal')
            }
        }
    })

    // 2. Set initial UI state (expanding the tree to current page)
    setSelectedVars()

    // 3. Handle Propagation Initial State
    if (propagatedContentId) {
        originalPropagatedPageIds.value = siteTreeStore.propagationData
            .map(o => o.pageId)
            .sort()

        await pageTemplatesContentAreasStore.init()
    }
})
</script>

<template>
    <div class="modal-dialog modal-lg site-tree-modal">
        <div class="modal-content">
            <div class="modal-header">
                <SiteTreeNavigationMenu
                    :site-tree-id="siteTreeId"
                    class="w-auto"
                />

                <button
                    aria-label="Close"
                    class="btn-close"
                    type="button"
                    @click="$emit('hide-modal')"
                ></button>
            </div>

            <div class="modal-body">
                <SiteTree :site-tree-id="siteTreeId"/>
            </div>

            <div v-if="propagatedContentId" class="modal-footer">
                <form @submit.prevent="saveSelectedPageIds">
                    <Button
                        type="submit"
                        class="ms-1 border"
                        :disabled="isSaveBtnDisabled"
                        :active-class="'btn-warning'"
                        :active-value="!isSaveBtnDisabled"
                    >
                        Save
                    </Button>
                </form>
            </div>
        </div>
    </div>
</template>